import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "./DashboardLayout";
import { api, API_URL, getToken } from "@/lib/api";
import { Waveform, StaticWaveform } from "@/components/landing/Waveform";
import { Play, Pause, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const API_BASE = API_URL || "";

const PLAN_BADGE = {
    free:    { label: "Free",    bg: "bg-cream-300",    text: "text-ink-muted" },
    starter: { label: "Starter", bg: "bg-[#7B3FE4]/10", text: "text-[#7B3FE4]" },
    pro:     { label: "Pro",     bg: "bg-coral/10",     text: "text-coral" },
};

export default function VoicesDashboard() {
    const [voices, setVoices] = useState([]);
    const [playingId, setPlayingId] = useState(null);
    const [loadingId, setLoadingId] = useState(null);
    const audioRef = useRef(null);

    useEffect(() => {
        api.get("/voices").then(r => setVoices(r.data)).catch(() => {});
        return () => audioRef.current?.pause();
    }, []);

    const onPlay = async (v) => {
        if (playingId === v.id) {
            audioRef.current?.pause();
            setPlayingId(null);
            return;
        }
        audioRef.current?.pause();
        setLoadingId(v.id);
        try {
            const url = `/demos/demo_${v.id}.wav?v=2`;
            const audio = new Audio(url);
            audioRef.current = audio;
            audio.onended = () => setPlayingId(null);
            audio.onerror = () => { toast.error("Demo audio not found"); setPlayingId(null); };
            await audio.play();
            setPlayingId(v.id);
        } catch {
            toast.info("Could not play demo");
        } finally {
            setLoadingId(null);
        }
    };

    return (
        <div data-testid="voices-dashboard">
            <PageHeader
                eyebrow="Voices"
                title="Pick the voice that fits."
                subtitle="Click play to preview. Set a default per article in the editor."
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                {voices.map(v => {
                    const playing = playingId === v.id;
                    const badge = PLAN_BADGE[v.min_plan] || PLAN_BADGE.free;
                    return (
                        <div key={v.id} data-testid={`dash-voice-${v.id}`} className="bg-white rounded-3xl p-6 border border-cream-300 shadow-soft hover:shadow-soft-lg transition-all">
                            <div className="flex items-start justify-between mb-4">
                                <div className="w-14 h-14 rounded-2xl flex items-center justify-center font-display text-[24px] font-bold text-white" style={{background: v.color}}>
                                    {v.name[0]}
                                </div>
                                <button
                                    onClick={() => onPlay(v)}
                                    data-testid={`preview-${v.id}`}
                                    className="w-10 h-10 rounded-full bg-ink hover:bg-coral text-cream-100 flex items-center justify-center transition-colors"
                                    aria-label={`Preview ${v.name}`}
                                >
                                    {loadingId === v.id
                                        ? <Loader2 size={13} className="animate-spin"/>
                                        : playing ? <Pause size={13} fill="currentColor"/>
                                        : <Play size={13} className="ml-0.5" fill="currentColor"/>
                                    }
                                </button>
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                                <h3 className="font-display text-[24px] font-semibold tracking-tight text-ink leading-none">{v.name}</h3>
                                <span className={`text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${badge.bg} ${badge.text}`}>
                                    {badge.label}
                                </span>
                            </div>
                            <p className="text-[11.5px] text-ink-muted mb-3 capitalize">{v.gender} · {v.accent}</p>
                            <p className="text-[13.5px] text-ink leading-relaxed mb-4">{v.tone}</p>
                            <div className="h-7">
                                {playing ? <Waveform bars={26} color={v.color} height={26}/> : <StaticWaveform bars={26} color="#0A0A0A" height={26}/>}
                            </div>
                        </div>
                    );
                })}

                {/* Locked voice teaser if on Free plan */}
                {voices.length <= 4 && (
                    <div className="bg-white rounded-3xl p-6 border border-dashed border-cream-300 flex flex-col items-center justify-center gap-3 text-center min-h-[220px]">
                        <Lock size={22} className="text-ink-muted"/>
                        <p className="text-[13px] font-semibold text-ink">4 more voices</p>
                        <p className="text-[12px] text-ink-muted">Mira, Sohel, Pratibha & Kabir unlock on Starter+</p>
                        <Link to="/dashboard/billing" className="text-[12px] font-semibold text-coral hover:underline">Upgrade →</Link>
                    </div>
                )}
            </div>

            <div className="mt-8 bg-ink-pill text-cream-100 rounded-3xl p-7 lg:p-9 flex flex-col lg:flex-row items-center justify-between gap-5">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.22em] font-semibold text-coral">Custom voice</p>
                    <h3 className="font-display text-[28px] lg:text-[36px] font-semibold tracking-tight mt-1">
                        Train your anchor's <span className="italic-display text-coral">actual voice.</span>
                    </h3>
                    <p className="mt-2 text-[14px] text-cream-100/70 max-w-md">30 minutes of clean recording, signed consent, 2-week turnaround. Available on Business + Enterprise.</p>
                </div>
                <button className="bg-cream-100 hover:bg-coral hover:text-white text-ink rounded-full px-7 py-3.5 text-[14px] font-semibold whitespace-nowrap transition-colors">
                    Request custom voice
                </button>
            </div>
        </div>
    );
}
